import React, { useState } from 'react';
import axios from 'axios';
import { useQuran } from '../context/QuranContext';
import { Search, Sparkles, X, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AISearch = () => {
    const { goToVerse, surahs, startRootBrowsing } = useQuran();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            toast.error('Please describe the verse you are looking for');
            return;
        }

        setIsSearching(true);
        setHasSearched(true);

        try {
            const response = await axios.post(`${API}/ai/search-verses`, {
                query: searchQuery.trim()
            });

            setSearchResults(response.data.results || []);

            if (response.data.results?.length === 0) {
                toast.info('No matching verses found. Try describing differently.');
            } else {
                toast.success(`Found ${response.data.results.length} matching verse(s)`);
            }
        } catch (err) {
            console.error('AI Search failed:', err);
            toast.error('Search failed. Please try again.');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleVerseClick = (verseKey, index) => {
        const [surahId, verseNum] = verseKey.split(':').map(Number);

        // Create a pseudo-word object for the browser
        const searchWord = {
            text_uthmani: searchQuery.substring(0, 30) + (searchQuery.length > 30 ? '...' : ''),
            translation: { text: 'AI Search' }
        };

        // Start browsing mode with search results (type: 'search')
        startRootBrowsing(searchWord, searchResults, index, 'search');
        goToVerse(surahId, verseNum);
        setIsOpen(false);
    };

    const getSurahName = (surahId) => {
        const surah = surahs.find(s => s.id === surahId);
        return surah ? surah.name_simple : `Surah ${surahId}`;
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSearch();
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setHasSearched(false);
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="border-[#8b5cf6] text-[#8b5cf6] hover:bg-[#8b5cf6]/10 hover:text-[#a78bfa] gap-2"
                    data-testid="ai-search-btn"
                >
                    <Sparkles className="w-4 h-4" />
                    <span className="hidden sm:inline">AI Search</span>
                </Button>
            </DialogTrigger>

            <DialogContent className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] max-w-2xl max-h-[85vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#f8fafc]">
                        <Sparkles className="w-5 h-5 text-[#8b5cf6]" />
                        AI Verse Search
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Search Input */}
                    <div className="space-y-2">
                        <Textarea
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Describe the verse you're looking for...&#10;&#10;Examples:&#10;• The verse about Moses and the burning bush&#10;• Where Allah talks about the creation of humans from clay&#10;• Root Mercy and Punishment in same verse&#10;• Story of Yusuf being thrown into the well"
                            className="bg-[#09090b] border-[#1e293b] text-[#f8fafc] min-h-[120px] placeholder:text-[#475569]"
                            disabled={isSearching}
                        />
                        <p className="text-xs text-[#64748b]">
                            Press Ctrl+Enter to search, or click the button below
                        </p>
                    </div>

                    {/* Search Button */}
                    <div className="flex gap-2">
                        <Button
                            onClick={handleSearch}
                            disabled={isSearching || !searchQuery.trim()}
                            className="flex-1 bg-gradient-to-r from-[#8b5cf6] to-[#6366f1] text-white hover:from-[#a78bfa] hover:to-[#818cf8]"
                        >
                            {isSearching ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Searching Quran...
                                </>
                            ) : (
                                <>
                                    <Search className="w-4 h-4 mr-2" />
                                    Find Verses
                                </>
                            )}
                        </Button>
                        {hasSearched && (
                            <Button
                                onClick={clearSearch}
                                variant="outline"
                                className="border-[#475569] text-[#94a3b8]"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        )}
                    </div>

                    {/* Search Results */}
                    {hasSearched && !isSearching && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-[#94a3b8]">
                                    {searchResults.length > 0
                                        ? `Found ${searchResults.length} matching verse(s)`
                                        : 'No verses found'
                                    }
                                </p>
                                {searchResults.length > 0 && (
                                    <p className="text-xs text-[#64748b]">
                                        Click to navigate
                                    </p>
                                )}
                            </div>

                            <ScrollArea className="h-[300px] pr-4">
                                <div className="space-y-3">
                                    {searchResults.map((result, index) => (
                                        <div
                                            key={index}
                                            className="p-4 rounded-lg border border-[#1e293b] hover:border-[#8b5cf6]/50 transition-colors cursor-pointer bg-[#09090b]/50 active:bg-[#1e293b]"
                                            onClick={() => handleVerseClick(result.verse_key, index)}
                                            data-testid={`search-result-${index}`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-1 text-xs rounded bg-[#8b5cf6]/20 text-[#8b5cf6] font-medium">
                                                        {result.surah_name || getSurahName(parseInt(result.verse_key.split(':')[0]))}
                                                    </span>
                                                    <span className="text-[#94a3b8] text-sm">
                                                        {result.verse_key}
                                                    </span>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-[#475569]" />
                                            </div>

                                            {result.arabic_text && (
                                                <p className="text-[#e2e8f0] text-base leading-relaxed arabic-text mb-2" dir="rtl">
                                                    {result.arabic_text.length > 150
                                                        ? result.arabic_text.substring(0, 150) + '...'
                                                        : result.arabic_text
                                                    }
                                                </p>
                                            )}

                                            {result.reason && (
                                                <p className="text-[#94a3b8] text-xs italic border-t border-[#1e293b] pt-2 mt-2">
                                                    Match: {result.reason}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AISearch;
